// src/components/PackageManagement.js
import React, { useState, useEffect } from "react";
import { validateForm, productValidationSchema } from "../utils/validation";
import {
  AUTO_FILL_UPGRADE_AMOUNT,
  CURRENT_ACTIVATION_FEE,
  isCurrentTierActivationFee,
  LEGACY_ACTIVATION_FEE,
} from "../config/membershipConfig";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ShoppingCart,
  Refresh,
  ExpandMore,
  Image as ImageIcon,
  AttachMoney,
  TrendingUp,
} from "@mui/icons-material";
import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
} from "firebase/firestore";
import { db } from "../firebase/config";

const PackageManagement = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);

  // Helper function to validate URLs
  const isValidUrl = (urlString) => {
    try {
      new URL(urlString);
      return true;
    } catch (_) {
      return false;
    }
  };

  // Helper function to generate unique SKU
  const generateSKU = (itemName, category = "") => {
    const cleanName = itemName.toLowerCase()
      .replace(/[^a-z0-9]/g, '')
      .substring(0, 8);
    const cleanCategory = category.toLowerCase()
      .replace(/[^a-z0-9]/g, '')
      .substring(0, 3);
    const timestamp = Date.now().toString().slice(-6);
    const random = Math.random().toString(36).substring(2, 5).toUpperCase();
    
    return `${cleanCategory ? cleanCategory + '-' : ''}${cleanName}-${timestamp}${random}`.toUpperCase();
  };
  const [packageForm, setPackageForm] = useState({
    name: "",
    description: "",
    price: "",
    category: "",
    features: "",
    imageUrl: "",
    isActive: true,
    hasSubItems: false,
    totalPackageValue: "",
    subItems: [],
    hasMetaWhaleAccount: false,
    metaWhaleAccountFee: "500",
  });
  const [validationErrors, setValidationErrors] = useState({});

  const categories = [
    "Cryptocurrency Trading",
    "E-commerce Products", 
    "Digital Services",
    "Investment Plans",
    "Premium Features",
    "Educational Courses",
    "Meta Whale",
  ];

  // Fetch all packages
  const fetchPackages = async () => {
    try {
      setLoading(true);
      setError("");

      const packagesQuery = query(
        collection(db, "packages"),
        orderBy("createdAt", "desc")
      );
      const snapshot = await getDocs(packagesQuery);
      const packagesData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setPackages(packagesData);
    } catch (error) {
      setError("Failed to fetch packages: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackages();
  }, []);

  // Handle form input changes
  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setPackageForm((prev) => ({
      ...prev,
      [field]: value,
    }));
    
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  // Open dialog for adding new package
  const handleAddPackage = () => {
    setEditingPackage(null);
    setPackageForm({
      name: "",
      description: "",
      price: "",
      category: "",
      features: "",
      imageUrl: "",
      isActive: true,
      hasSubItems: false,
      totalPackageValue: "",
      subItems: [],
      hasMetaWhaleAccount: false,
      metaWhaleAccountFee: "500",
      });
    setValidationErrors({});
    setDialogOpen(true);
  };

  // Open dialog for editing existing package
  const handleEditPackage = (pkg) => {
    setEditingPackage(pkg);
    setPackageForm({
      name: pkg.name,
      description: pkg.description,
      price: pkg.price.toString(),
      category: pkg.category,
      features: pkg.features.join("\n"),
      imageUrl: pkg.imageUrl || "",
      isActive: pkg.isActive,
      hasSubItems: pkg.hasSubItems || false,
      totalPackageValue: pkg.totalPackageValue?.toString() || "",
      subItems: (pkg.subItems || []).map(item => ({
        id: item.id || Date.now() + Math.random(),
        itemName: item.itemName || "",
        itemPrice: item.itemPrice?.toString() || "", // This will become the package price
        // New pricing fields for discount calculation
        basePrice: item.basePrice?.toString() || "",
        profitMargin: item.profitMargin?.toString() || "",
        sellingPrice: item.sellingPrice?.toString() || "",
        compareAtPrice: item.compareAtPrice?.toString() || "",
        gstPercentage: (item.gstPercentage !== undefined && item.gstPercentage !== null) ? item.gstPercentage.toString() : "18",
        minQuantity: item.minQuantity?.toString() || "1",
        maxQuantity: item.maxQuantity?.toString() || "",
        // Load comprehensive item details with fallbacks
        description: item.description || "",
        category: item.category || "",
        brand: item.brand || "",
        sizes: item.sizes || [],
        colors: item.colors || [],
        weight: item.weight || "",
        dimensions: item.dimensions || "",
        material: item.material || "",
        specifications: item.specifications || "",
        imageUrl: item.imageUrl || "",
        sku: item.sku || "",
        barcode: item.barcode || "",
        warranty: item.warranty || "",
        countryOfOrigin: item.countryOfOrigin || "India",
        manufacturer: item.manufacturer || "",
        isFragile: item.isFragile || false,
        requiresAssembly: item.requiresAssembly || false,
        careInstructions: item.careInstructions || "",
        tags: item.tags || [],
        features: item.features || [],
        hsnCode: item.hsnCode || "",
      })),
      hasMetaWhaleAccount: pkg.hasMetaWhaleAccount || false,
      metaWhaleAccountFee: pkg.metaWhaleAccountFee?.toString() || "500",
    });
    setValidationErrors({});
    setDialogOpen(true);
  };

  // Handle sub-items management
  const handleAddSubItem = () => {
    setPackageForm(prev => ({
      ...prev,
      subItems: [...prev.subItems, {
        id: Date.now(),
        itemName: "",
        itemPrice: "", // Package price for this item
        // New pricing fields for discount calculation
        basePrice: "",
        profitMargin: "",
        sellingPrice: "",
        compareAtPrice: "",
        gstPercentage: "18",
        minQuantity: "1",
        maxQuantity: "",
        // Add comprehensive item details
        description: "",
        category: "",
        brand: "",
        sizes: [],
        colors: [],
        weight: "",
        dimensions: "",
        material: "",
        specifications: "",
        imageUrl: "",
        sku: "",
        barcode: "",
        warranty: "",
        countryOfOrigin: "India",
        manufacturer: "",
        isFragile: false,
        requiresAssembly: false,
        careInstructions: "",
        tags: [],
        features: [],
        hsnCode: "",
      }]
    }));
  };

  const handleRemoveSubItem = (subItemId) => {
    setPackageForm(prev => ({
      ...prev,
      subItems: prev.subItems.filter(item => item.id !== subItemId)
    }));
  };

  const handleSubItemChange = (subItemId, field, value) => {
    setPackageForm(prev => ({
      ...prev,
      subItems: prev.subItems.map(item => {
        if (item.id === subItemId) {
          const updatedItem = { ...item, [field]: value };
          
          // Auto-generate SKU when item name changes and SKU is empty
          if (field === 'itemName' && value.trim() && !item.sku) {
            updatedItem.sku = generateSKU(value, item.category);
          }
          
          // Auto-generate SKU when category changes and item name exists but SKU is empty
          if (field === 'category' && value.trim() && item.itemName && !item.sku) {
            updatedItem.sku = generateSKU(item.itemName, value);
          }
          
          // Auto-calculate selling price when base price or profit margin changes
          const basePrice = parseFloat(field === 'basePrice' ? value : updatedItem.basePrice || 0);
          const profitMargin = parseFloat(field === 'profitMargin' ? value : updatedItem.profitMargin || 0);
          
          if ((field === 'basePrice' || field === 'profitMargin') && 
              !isNaN(basePrice) && !isNaN(profitMargin) && 
              (basePrice > 0 || profitMargin > 0)) {
            updatedItem.sellingPrice = (basePrice + profitMargin).toFixed(2);
          }
          
          return updatedItem;
        }
        return item;
      })
    }));
  };

  // Save package (add or update)
  const handleSavePackage = async () => {
    try {
      setError("");
      setValidationErrors({});

      // Validate form data
      const validation = validateForm(packageForm, productValidationSchema);

      if (!validation.isValid) {
        setValidationErrors(validation.errors);
        setError("Please correct the errors in the form");
        return;
      }

      // Use sanitized data from validation
      const sanitizedData = validation.sanitizedData;

      // Additional validation for sub-items packages
      if (packageForm.hasSubItems) {
        if (!packageForm.totalPackageValue || parseFloat(packageForm.totalPackageValue) <= 0) {
          setError("Total package value is required for packages with sub-items");
          return;
        }
        if (packageForm.subItems.length === 0) {
          setError("At least one sub-item is required for packages with sub-items");
          return;
        }
        
        // Validate each sub-item with enhanced validation
        for (let i = 0; i < packageForm.subItems.length; i++) {
          const item = packageForm.subItems[i];
          const itemIndex = i + 1;
          
          // Required fields validation
          if (!item.itemName?.trim()) {
            setError(`Sub-item ${itemIndex}: Item name is required`);
            return;
          }
          if (!item.itemPrice || parseFloat(item.itemPrice) <= 0) {
            setError(`Sub-item ${itemIndex}: Valid item price is required`);
            return;
          }
          if (!item.description?.trim()) {
            setError(`Sub-item ${itemIndex}: Description is required`);
            return;
          }
          if (!item.category?.trim()) {
            setError(`Sub-item ${itemIndex}: Category is required`);
            return;
          }
          
          // Quantity validation
          if (parseInt(item.minQuantity || "1") < 1) {
            setError(`Sub-item ${itemIndex}: Minimum quantity must be at least 1`);
            return;
          }
          if (item.maxQuantity && parseInt(item.maxQuantity) < parseInt(item.minQuantity || "1")) {
            setError(`Sub-item ${itemIndex}: Maximum quantity must be greater than or equal to minimum quantity`);
            return;
          }
          
          // GST validation
          if (!item.gstPercentage || parseFloat(item.gstPercentage) < 0 || parseFloat(item.gstPercentage) > 100) {
            setError(`Sub-item ${itemIndex}: GST percentage must be between 0 and 100`);
            return;
          }
          
          // Optional field validation
          if (item.imageUrl?.trim() && !isValidUrl(item.imageUrl.trim())) {
            setError(`Sub-item ${itemIndex}: Invalid image URL format`);
            return;
          }
          if (item.sku && item.sku.length < 3) {
            setError(`Sub-item ${itemIndex}: SKU must be at least 3 characters long`);
            return;
          }
        }
      }

      const packageData = {
        name: sanitizedData.name,
        description: sanitizedData.description,
        price: sanitizedData.price,
        category: sanitizedData.category,
        imageUrl: sanitizedData.imageUrl || "",
        features: packageForm.features
          .split("\n")
          .filter((f) => f.trim())
          .map((f) => f.trim()),
        isActive: packageForm.isActive,
        hasSubItems: packageForm.hasSubItems,
        totalPackageValue: packageForm.hasSubItems ? parseFloat(packageForm.totalPackageValue) : null,
        subItems: packageForm.hasSubItems ? packageForm.subItems.map(item => ({
          id: item.id,
          itemName: item.itemName.trim(),
          // itemPrice should be the selling price (base + profit), not including GST
          itemPrice: item.sellingPrice ? parseFloat(item.sellingPrice) : (item.itemPrice ? parseFloat(item.itemPrice) : 0),
          // Add new pricing fields for discount calculation
          basePrice: item.basePrice ? parseFloat(item.basePrice) : 0,
          profitMargin: item.profitMargin ? parseFloat(item.profitMargin) : 0,
          sellingPrice: item.sellingPrice ? parseFloat(item.sellingPrice) : 0,
          compareAtPrice: item.compareAtPrice ? parseFloat(item.compareAtPrice) : 0,
          gstPercentage: parseFloat(item.gstPercentage),
          minQuantity: parseInt(item.minQuantity || "1"),
          maxQuantity: item.maxQuantity ? parseInt(item.maxQuantity) : null,
          // Add comprehensive item details
          description: item.description?.trim() || "",
          category: item.category?.trim() || "",
          brand: item.brand?.trim() || "",
          sizes: Array.isArray(item.sizes) ? item.sizes : [],
          colors: Array.isArray(item.colors) ? item.colors : [],
          weight: item.weight?.trim() || "",
          dimensions: item.dimensions?.trim() || "",
          material: item.material?.trim() || "",
          specifications: item.specifications?.trim() || "",
          imageUrl: item.imageUrl?.trim() || "",
          sku: item.sku?.trim() || "",
          barcode: item.barcode?.trim() || "",
          warranty: item.warranty?.trim() || "",
          countryOfOrigin: item.countryOfOrigin || "India",
          manufacturer: item.manufacturer?.trim() || "",
          isFragile: item.isFragile || false,
          requiresAssembly: item.requiresAssembly || false,
          careInstructions: item.careInstructions?.trim() || "",
          tags: Array.isArray(item.tags) ? item.tags : [],
          features: Array.isArray(item.features) ? item.features : [],
          hsnCode: item.hsnCode?.trim() || "",
        })) : [],
        activationFee: (() => {
          if (packageForm.hasSubItems) return CURRENT_ACTIVATION_FEE;
          const priceNum = Number(sanitizedData.price);
          if (priceNum === LEGACY_ACTIVATION_FEE) return LEGACY_ACTIVATION_FEE;
          if (priceNum === AUTO_FILL_UPGRADE_AMOUNT) return AUTO_FILL_UPGRADE_AMOUNT;
          if (isCurrentTierActivationFee(priceNum)) return CURRENT_ACTIVATION_FEE;
          return null;
        })(),
        hasMetaWhaleAccount: packageForm.hasMetaWhaleAccount,
        metaWhaleAccountFee: packageForm.hasMetaWhaleAccount ? parseFloat(packageForm.metaWhaleAccountFee) : null,
        updatedAt: new Date().toISOString(),
      };

      if (editingPackage) {
        // Update existing package
        await updateDoc(doc(db, "packages", editingPackage.id), packageData);
        setSuccess("Package updated successfully!");
      } else {
        // Add new package
        packageData.createdAt = new Date().toISOString();
        await addDoc(collection(db, "packages"), packageData);
        setSuccess("Package added successfully!");
      }

      setDialogOpen(false);
      fetchPackages();
      
      // Clear any lingering messages after a delay
      setTimeout(() => {
        setSuccess("");
        setError("");
      }, 3000);
    } catch (error) {
      setError("Failed to save package: " + error.message);
    }
  };

  // Delete package
  const handleDeletePackage = async (packageId) => {
    if (window.confirm("Are you sure you want to delete this package?")) {
      try {
        await deleteDoc(doc(db, "packages", packageId));
        setSuccess("Package deleted successfully!");
        fetchPackages();
      } catch (error) {
        setError("Failed to delete package: " + error.message);
      }
    }
  };

  // Toggle package status
  const handleToggleStatus = async (pkg) => {
    try {
      await updateDoc(doc(db, "packages", pkg.id), {
        isActive: !pkg.isActive,
        updatedAt: new Date().toISOString(),
      });
      setSuccess(
        `Package ${!pkg.isActive ? "activated" : "deactivated"} successfully!`
      );
      fetchPackages();
    } catch (error) {
      setError("Failed to update package status: " + error.message);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" gutterBottom>
          Package Management
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchPackages}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddPackage}
          >
            Add Package
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      {/* Statistics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <ShoppingCart color="primary" sx={{ fontSize: 48, mb: 1 }} />
              <Typography variant="h4" color="primary">
                {packages.length}
              </Typography>
              <Typography variant="body1">Total Packages</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <ShoppingCart color="success" sx={{ fontSize: 48, mb: 1 }} />
              <Typography variant="h4" color="success.main">
                {packages.filter((p) => p.isActive).length}
              </Typography>
              <Typography variant="body1">Active Packages</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <ShoppingCart color="error" sx={{ fontSize: 48, mb: 1 }} />
              <Typography variant="h4" color="error.main">
                {packages.filter((p) => !p.isActive).length}
              </Typography>
              <Typography variant="body1">Inactive Packages</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Packages Table */}
      {loading ? (
        <Box textAlign="center" sx={{ mt: 4 }}>
          <CircularProgress />
          <Typography variant="body1" sx={{ mt: 2 }}>
            Loading packages...
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Package Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Price/Value (INR)</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Features/Sub-items</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {packages.map((pkg) => (
                <TableRow key={pkg.id}>
                  <TableCell>
                    <Typography variant="subtitle2">{pkg.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {pkg.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={pkg.category} size="small" />
                  </TableCell>
                  <TableCell>
                    {pkg.hasSubItems ? (
                      <Box>
                        <Typography variant="h6" color="primary">
                          {formatPrice(pkg.totalPackageValue)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Min. Value
                        </Typography>
                        <br />
                        <Typography variant="caption" color="success.main">
                          + ₹{CURRENT_ACTIVATION_FEE} Activation
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="h6" color="primary">
                        {formatPrice(pkg.price)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={pkg.hasSubItems ? "Sub-items" : "Regular"} 
                      color={pkg.hasSubItems ? "secondary" : "default"}
                      size="small" 
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={pkg.isActive ? "Active" : "Inactive"}
                      color={pkg.isActive ? "success" : "error"}
                      size="small"
                      onClick={() => handleToggleStatus(pkg)}
                      style={{ cursor: "pointer" }}
                    />
                  </TableCell>
                  <TableCell>
                    {pkg.hasSubItems ? (
                      <Box>
                        <Typography variant="body2" color="primary">
                          {pkg.subItems?.length || 0} sub-items
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {pkg.subItems?.slice(0, 2).map(item => item.itemName).join(", ")}
                          {pkg.subItems?.length > 2 && "..."}
                        </Typography>
                        
                        {/* Show sub-item images preview */}
                        {pkg.subItems?.some(item => item.imageUrl) && (
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                            {pkg.subItems.slice(0, 3).map((item, index) => 
                              item.imageUrl ? (
                                <img
                                  key={index}
                                  src={item.imageUrl}
                                  alt={item.itemName}
                                  style={{
                                    width: '24px',
                                    height: '24px',
                                    objectFit: 'cover',
                                    borderRadius: '4px',
                                    border: '1px solid #ddd'
                                  }}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              ) : null
                            )}
                            {pkg.subItems.filter(item => item.imageUrl).length > 3 && (
                              <Typography variant="caption" sx={{ alignSelf: 'center', ml: 0.5 }}>
                                +{pkg.subItems.filter(item => item.imageUrl).length - 3} more
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2">
                        {pkg.features.slice(0, 2).join(", ")}
                        {pkg.features.length > 2 && "..."}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{formatDate(pkg.createdAt)}</TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => handleEditPackage(pkg)}
                      color="primary"
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDeletePackage(pkg.id)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {packages.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body1" color="text.secondary">
                      No packages found. Click "Add Package" to create your
                      first package.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Package Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setError("");
          setSuccess("");
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingPackage ? "Edit Package" : "Add New Package"}
        </DialogTitle>
        <DialogContent>
          {/* Error Alert inside Modal */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          {/* Success Alert inside Modal */}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
              {success}
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Package Name *"
                value={packageForm.name}
                onChange={handleInputChange("name")}
                placeholder="e.g., Premium Trading Plan"
                error={!!validationErrors.name}
                helperText={validationErrors.name}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Category *</InputLabel>
                <Select
                  value={packageForm.category}
                  onChange={handleInputChange("category")}
                >
                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description *"
                value={packageForm.description}
                onChange={handleInputChange("description")}
                multiline
                rows={2}
                placeholder="Describe what this package offers..."
                error={!!validationErrors.description}
                helperText={validationErrors.description}
              />
            </Grid>

            {/* Toggle for Package contains sub-items */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={packageForm.hasSubItems}
                    onChange={(e) => setPackageForm(prev => ({ 
                      ...prev, 
                      hasSubItems: e.target.checked,
                      subItems: e.target.checked ? prev.subItems : [],
                      totalPackageValue: e.target.checked ? prev.totalPackageValue : ""
                    }))}
                    color="primary"
                  />
                }
                label="Package contains sub-items"
                sx={{ mt: 1, mb: 1 }}
              />
            </Grid>

            {packageForm.hasSubItems ? (
              <>
                {/* Total Package Value */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Total Package Value (INR) *"
                    value={packageForm.totalPackageValue}
                    onChange={handleInputChange("totalPackageValue")}
                    type="number"
                    placeholder="e.g., 10000"
                    helperText="Minimum amount customers must select in sub-items"
                    InputProps={{
                      startAdornment: "₹",
                    }}
                  />
                </Grid>
                
                {/* Activation Fee Display */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Activation Fee"
                    value={`₹${CURRENT_ACTIVATION_FEE}`}
                    disabled
                    helperText="Fixed activation fee (will be returned as cashback)"
                  />
                </Grid>

                {/* Meta Whale Account Toggle */}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={packageForm.hasMetaWhaleAccount}
                        onChange={(e) => setPackageForm(prev => ({ 
                          ...prev, 
                          hasMetaWhaleAccount: e.target.checked
                        }))}
                        color="primary"
                      />
                    }
                    label="Include Meta Whale Account Creation"
                    sx={{ mt: 1, mb: 1 }}
                  />
                </Grid>

                {/* Meta Whale Account Fee */}
                {packageForm.hasMetaWhaleAccount && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Meta Whale Account Fee (INR)"
                      value={packageForm.metaWhaleAccountFee}
                      onChange={handleInputChange("metaWhaleAccountFee")}
                      type="number"
                      placeholder="500"
                      helperText="Fee for Meta Whale account creation"
                      InputProps={{
                        startAdornment: "₹",
                      }}
                    />
                  </Grid>
                )}


                {/* Sub-items Section */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }}>
                    <Typography variant="h6" color="primary">
                      Sub-items Configuration
                    </Typography>
                  </Divider>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1">
                      Sub-items ({packageForm.subItems.length})
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={handleAddSubItem}
                      size="small"
                    >
                      Add Sub-item
                    </Button>
                  </Box>

                  {packageForm.subItems.map((subItem, index) => (
                    <Card key={subItem.id} sx={{ mb: 3, p: 3, bgcolor: 'grey.50', border: '1px solid #e0e0e0' }}>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        Sub-item {index + 1}
                        <IconButton
                          onClick={() => handleRemoveSubItem(subItem.id)}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Typography>
                      
                      <Grid container spacing={2}>
                        {/* Basic Info */}
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Item Name *"
                            value={subItem.itemName}
                            onChange={(e) => handleSubItemChange(subItem.id, 'itemName', e.target.value)}
                            placeholder="e.g., Trading Bot License"
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField
                              fullWidth
                              label="SKU"
                              value={subItem.sku || ""}
                              onChange={(e) => handleSubItemChange(subItem.id, 'sku', e.target.value)}
                              placeholder="Auto-generated or enter custom"
                              helperText="Automatically generated from item name or enter custom"
                              size="small"
                            />
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => {
                                const newSKU = generateSKU(subItem.itemName || 'Item', subItem.category);
                                handleSubItemChange(subItem.id, 'sku', newSKU);
                              }}
                              disabled={!subItem.itemName}
                              sx={{ 
                                minWidth: '100px',
                                height: '40px',
                                mt: 0.5
                              }}
                            >
                              Generate
                            </Button>
                          </Box>
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Description"
                            value={subItem.description || ""}
                            onChange={(e) => handleSubItemChange(subItem.id, 'description', e.target.value)}
                            placeholder="Detailed description of the sub-item"
                            multiline
                            rows={2}
                            size="small"
                          />
                        </Grid>

                        {/* Category and Brand */}
                        <Grid item xs={12} md={4}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Category</InputLabel>
                            <Select
                              value={subItem.category || ""}
                              onChange={(e) => handleSubItemChange(subItem.id, 'category', e.target.value)}
                              label="Category"
                            >
                              {categories.map((category) => (
                                <MenuItem key={category} value={category}>
                                  {category}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Brand"
                            value={subItem.brand || ""}
                            onChange={(e) => handleSubItemChange(subItem.id, 'brand', e.target.value)}
                            placeholder="Brand name"
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Manufacturer"
                            value={subItem.manufacturer || ""}
                            onChange={(e) => handleSubItemChange(subItem.id, 'manufacturer', e.target.value)}
                            placeholder="Manufacturing company"
                            size="small"
                          />
                        </Grid>

                        {/* Pricing Section */}
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main', fontWeight: 600 }}>
                            💰 Pricing Details (for discount calculation)
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            label="Base Price (₹)"
                            value={subItem.basePrice}
                            onChange={(e) => handleSubItemChange(subItem.id, 'basePrice', e.target.value)}
                            type="number"
                            placeholder="Cost price"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <AttachMoney />
                                </InputAdornment>
                              ),
                            }}
                            size="small"
                          />
                        </Grid>
                        
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            label="Profit Margin (₹)"
                            value={subItem.profitMargin}
                            onChange={(e) => handleSubItemChange(subItem.id, 'profitMargin', e.target.value)}
                            type="number"
                            placeholder="Profit amount"
                            helperText="Profit in rupees"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <TrendingUp />
                                </InputAdornment>
                              ),
                            }}
                            size="small"
                          />
                        </Grid>
                        
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            label="Selling Price (₹)"
                            value={subItem.sellingPrice}
                            disabled
                            placeholder="Auto-calculated"
                            helperText="Base + Profit Margin"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <AttachMoney />
                                </InputAdornment>
                              ),
                            }}
                            size="small"
                            sx={{
                              '& .MuiInputBase-input.Mui-disabled': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                              },
                            }}
                          />
                        </Grid>
                        
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            label="MRP/Compare At (₹)"
                            value={subItem.compareAtPrice}
                            onChange={(e) => handleSubItemChange(subItem.id, 'compareAtPrice', e.target.value)}
                            type="number"
                            placeholder="Market price"
                            helperText="Original retail price"
                            size="small"
                          />
                        </Grid>
                        
                        {/* GST Field - Made more prominent */}
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" sx={{ mb: 1, color: 'error.main', fontWeight: 600 }}>
                            📊 Tax Configuration
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="GST Percentage (%) *"
                            value={subItem.gstPercentage}
                            onChange={(e) => handleSubItemChange(subItem.id, 'gstPercentage', e.target.value)}
                            type="number"
                            placeholder="18"
                            helperText="GST tax percentage (required for calculations)"
                            inputProps={{ min: 0, max: 100, step: 0.01 }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  %
                                </InputAdornment>
                              ),
                            }}
                            size="small"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: 'rgba(255, 193, 7, 0.1)',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 193, 7, 0.15)',
                                },
                                '&.Mui-focused': {
                                  backgroundColor: 'rgba(255, 193, 7, 0.1)',
                                }
                              }
                            }}
                          />
                        </Grid>
                        
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="HSN Code"
                            value={subItem.hsnCode || ""}
                            onChange={(e) => handleSubItemChange(subItem.id, 'hsnCode', e.target.value)}
                            placeholder="e.g., 6109"
                            helperText="Harmonized System of Nomenclature code"
                            size="small"
                          />
                        </Grid>
                        
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" sx={{ mb: 1, color: 'success.main', fontWeight: 600 }}>
                            📦 Package Pricing
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            label="Min Qty *"
                            value={subItem.minQuantity || "1"}
                            onChange={(e) => handleSubItemChange(subItem.id, 'minQuantity', e.target.value)}
                            type="number"
                            placeholder="1"
                            inputProps={{ min: 1 }}
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            label="Max Qty"
                            value={subItem.maxQuantity || ""}
                            onChange={(e) => handleSubItemChange(subItem.id, 'maxQuantity', e.target.value)}
                            type="number"
                            placeholder="No limit"
                            inputProps={{ min: 1 }}
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            label="Warranty"
                            value={subItem.warranty || ""}
                            onChange={(e) => handleSubItemChange(subItem.id, 'warranty', e.target.value)}
                            placeholder="e.g., 1 year"
                            size="small"
                          />
                        </Grid>

                        {/* Physical Properties */}
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            label="Weight"
                            value={subItem.weight || ""}
                            onChange={(e) => handleSubItemChange(subItem.id, 'weight', e.target.value)}
                            placeholder="e.g., 500g"
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            label="Dimensions"
                            value={subItem.dimensions || ""}
                            onChange={(e) => handleSubItemChange(subItem.id, 'dimensions', e.target.value)}
                            placeholder="L x W x H"
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            label="Material"
                            value={subItem.material || ""}
                            onChange={(e) => handleSubItemChange(subItem.id, 'material', e.target.value)}
                            placeholder="e.g., Plastic, Metal"
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            label="Barcode"
                            value={subItem.barcode || ""}
                            onChange={(e) => handleSubItemChange(subItem.id, 'barcode', e.target.value)}
                            placeholder="Product barcode"
                            size="small"
                          />
                        </Grid>

                        {/* Image Section */}
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            Product Image
                          </Typography>
                          <TextField
                            fullWidth
                            label="Image URL"
                            value={subItem.imageUrl || ""}
                            onChange={(e) => handleSubItemChange(subItem.id, 'imageUrl', e.target.value)}
                            placeholder="https://example.com/image.jpg"
                            size="small"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <ImageIcon />
                                </InputAdornment>
                              ),
                            }}
                            helperText="Enter a direct URL to the product image"
                          />
                          
                          {/* Image Preview */}
                          {subItem.imageUrl && (
                            <Box sx={{ textAlign: "center", mt: 2 }}>
                              <Typography variant="body2" gutterBottom>
                                Image Preview:
                              </Typography>
                              <img
                                src={subItem.imageUrl}
                                alt={`${subItem.itemName} preview`}
                                style={{
                                  maxWidth: "150px",
                                  maxHeight: "150px",
                                  objectFit: "cover",
                                  borderRadius: "8px",
                                  border: "1px solid #ddd",
                                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                }}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                                onLoad={(e) => {
                                  e.target.style.display = "block";
                                }}
                              />
                            </Box>
                          )}
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Country of Origin</InputLabel>
                            <Select
                              value={subItem.countryOfOrigin || "India"}
                              onChange={(e) => handleSubItemChange(subItem.id, 'countryOfOrigin', e.target.value)}
                              label="Country of Origin"
                            >
                              <MenuItem value="India">India</MenuItem>
                              <MenuItem value="China">China</MenuItem>
                              <MenuItem value="USA">USA</MenuItem>
                              <MenuItem value="Germany">Germany</MenuItem>
                              <MenuItem value="Japan">Japan</MenuItem>
                              <MenuItem value="Other">Other</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        {/* Checkboxes for fragile and assembly */}
                        <Grid item xs={12} md={6}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={subItem.isFragile || false}
                                onChange={(e) => handleSubItemChange(subItem.id, 'isFragile', e.target.checked)}
                                size="small"
                              />
                            }
                            label="Fragile Item"
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={subItem.requiresAssembly || false}
                                onChange={(e) => handleSubItemChange(subItem.id, 'requiresAssembly', e.target.checked)}
                                size="small"
                              />
                            }
                            label="Requires Assembly"
                          />
                        </Grid>

                        {/* Expandable sections */}
                        <Grid item xs={12}>
                          <Accordion>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                              <Typography variant="body2">Additional Details</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Grid container spacing={2}>
                                <Grid item xs={12}>
                                  <TextField
                                    fullWidth
                                    label="Specifications"
                                    value={subItem.specifications || ""}
                                    onChange={(e) => handleSubItemChange(subItem.id, 'specifications', e.target.value)}
                                    placeholder="Technical specifications and features"
                                    multiline
                                    rows={3}
                                    size="small"
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <TextField
                                    fullWidth
                                    label="Care Instructions"
                                    value={subItem.careInstructions || ""}
                                    onChange={(e) => handleSubItemChange(subItem.id, 'careInstructions', e.target.value)}
                                    placeholder="How to care for this item"
                                    multiline
                                    rows={2}
                                    size="small"
                                  />
                                </Grid>
                                
                                {/* Colors and Sizes as simple text fields for now */}
                                <Grid item xs={12} md={6}>
                                  <TextField
                                    fullWidth
                                    label="Available Colors"
                                    value={Array.isArray(subItem.colors) ? subItem.colors.join(', ') : subItem.colors || ""}
                                    onChange={(e) => handleSubItemChange(subItem.id, 'colors', e.target.value.split(',').map(c => c.trim()).filter(c => c))}
                                    placeholder="Red, Blue, Green (comma separated)"
                                    size="small"
                                  />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <TextField
                                    fullWidth
                                    label="Available Sizes"
                                    value={Array.isArray(subItem.sizes) ? subItem.sizes.join(', ') : subItem.sizes || ""}
                                    onChange={(e) => handleSubItemChange(subItem.id, 'sizes', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                                    placeholder="S, M, L, XL (comma separated)"
                                    size="small"
                                  />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <TextField
                                    fullWidth
                                    label="Tags"
                                    value={Array.isArray(subItem.tags) ? subItem.tags.join(', ') : subItem.tags || ""}
                                    onChange={(e) => handleSubItemChange(subItem.id, 'tags', e.target.value.split(',').map(t => t.trim()).filter(t => t))}
                                    placeholder="electronics, gadget, premium (comma separated)"
                                    size="small"
                                  />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <TextField
                                    fullWidth
                                    label="Key Features"
                                    value={Array.isArray(subItem.features) ? subItem.features.join(', ') : subItem.features || ""}
                                    onChange={(e) => handleSubItemChange(subItem.id, 'features', e.target.value.split(',').map(f => f.trim()).filter(f => f))}
                                    placeholder="waterproof, wireless, portable (comma separated)"
                                    size="small"
                                  />
                                </Grid>
                              </Grid>
                            </AccordionDetails>
                          </Accordion>
                        </Grid>
                      </Grid>
                    </Card>
                  ))}

                  {packageForm.subItems.length === 0 && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      Click "Add Sub-item" to add items that customers can select to reach the package value.
                    </Alert>
                  )}
                </Grid>
              </>
            ) : (
              <>
                {/* Regular Package Fields */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Price (INR) *"
                    value={packageForm.price}
                    onChange={handleInputChange("price")}
                    type="number"
                    placeholder="e.g., 9999"
                    error={!!validationErrors.price}
                    helperText={validationErrors.price}
                    InputProps={{
                      startAdornment: "₹",
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={packageForm.isActive}
                      onChange={handleInputChange("isActive")}
                    >
                      <MenuItem value={true}>Active</MenuItem>
                      <MenuItem value={false}>Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Features (one per line)"
                    value={packageForm.features}
                    onChange={handleInputChange("features")}
                    multiline
                    rows={4}
                    placeholder="Advanced trading tools&#10;24/7 customer support&#10;Real-time market data&#10;Portfolio analytics"
                  />
                </Grid>
              </>
            )}

            {/* Common Fields */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Image URL (Optional)"
                value={packageForm.imageUrl}
                onChange={handleInputChange("imageUrl")}
                placeholder="https://example.com/image.jpg"
                error={!!validationErrors.imageUrl}
                helperText={validationErrors.imageUrl}
              />
            </Grid>

            {/* Status field for sub-items packages */}
            {packageForm.hasSubItems && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={packageForm.isActive}
                    onChange={handleInputChange("isActive")}
                  >
                    <MenuItem value={true}>Active</MenuItem>
                    <MenuItem value={false}>Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDialogOpen(false);
            setError("");
            setSuccess("");
          }}>Cancel</Button>
          <Button onClick={handleSavePackage} variant="contained">
            {editingPackage ? "Update Package" : "Create Package"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PackageManagement;
